/**
 * Order Store - Manage customer orders
 */
import { create } from 'zustand';
import { ordersAPI } from '../services/api';
import type { Order, CreateOrderRequest, UpdateOrderRequest, OrderStats } from '../types';

interface OrderState {
  orders: Order[];
  currentOrder: Order | null;
  stats: OrderStats | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchOrders: (projectId?: number, assignedProjectId?: number, status?: string) => Promise<void>;
  fetchOrderById: (orderId: number) => Promise<void>;
  createOrder: (data: CreateOrderRequest) => Promise<Order>;
  updateOrder: (orderId: number, data: UpdateOrderRequest) => Promise<void>;
  deleteOrder: (orderId: number) => Promise<void>;
  approveOrder: (orderId: number, assigned_project_id: number, internal_notes?: string) => Promise<void>;
  rejectOrder: (orderId: number, internal_notes: string) => Promise<void>;
  fetchStats: (projectId?: number, assignedProjectId?: number) => Promise<void>;
  clearOrders: () => void;
  setCurrentOrder: (order: Order | null) => void;
}

export const useOrderStore = create<OrderState>((set, get) => ({
  orders: [],
  currentOrder: null,
  stats: null,
  isLoading: false,
  error: null,

  fetchOrders: async (projectId?: number, assignedProjectId?: number, status?: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await ordersAPI.getAll(projectId, assignedProjectId, status);
      set({ orders: response.data, isLoading: false });
    } catch (error: any) {
      console.error('Error fetching orders:', error);
      set({
        error: error.response?.data?.detail || 'Chyba pri načítaní objednávok',
        isLoading: false
      });
    }
  },

  fetchOrderById: async (orderId: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await ordersAPI.getById(orderId);
      set({ currentOrder: response.data, isLoading: false });
    } catch (error: any) {
      console.error('Error fetching order:', error);
      set({
        error: error.response?.data?.detail || 'Chyba pri načítaní objednávky',
        isLoading: false
      });
    }
  },

  createOrder: async (data: CreateOrderRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await ordersAPI.create(data);
      const newOrder = response.data;

      // Add to orders list
      set(state => ({
        orders: [newOrder, ...state.orders],
        isLoading: false
      }));

      return newOrder;
    } catch (error: any) {
      console.error('Error creating order:', error);
      set({
        error: error.response?.data?.detail || 'Chyba pri vytváraní objednávky',
        isLoading: false
      });
      throw error;
    }
  },

  updateOrder: async (orderId: number, data: UpdateOrderRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await ordersAPI.update(orderId, data);
      const updatedOrder = response.data;

      // Update in orders list
      set(state => ({
        orders: state.orders.map(o => o.id === orderId ? updatedOrder : o),
        currentOrder: state.currentOrder?.id === orderId ? updatedOrder : state.currentOrder,
        isLoading: false
      }));
    } catch (error: any) {
      console.error('Error updating order:', error);
      set({
        error: error.response?.data?.detail || 'Chyba pri aktualizácii objednávky',
        isLoading: false
      });
      throw error;
    }
  },

  deleteOrder: async (orderId: number) => {
    set({ isLoading: true, error: null });
    try {
      await ordersAPI.delete(orderId);

      // Remove from orders list
      set(state => ({
        orders: state.orders.filter(o => o.id !== orderId),
        currentOrder: state.currentOrder?.id === orderId ? null : state.currentOrder,
        isLoading: false
      }));
    } catch (error: any) {
      console.error('Error deleting order:', error);
      set({
        error: error.response?.data?.detail || 'Chyba pri mazaní objednávky',
        isLoading: false
      });
      throw error;
    }
  },

  approveOrder: async (orderId: number, assigned_project_id: number, internal_notes?: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await ordersAPI.approve(orderId, assigned_project_id, internal_notes);
      const approvedOrder = response.data;

      // Update in orders list
      set(state => ({
        orders: state.orders.map(o => o.id === orderId ? approvedOrder : o),
        currentOrder: state.currentOrder?.id === orderId ? approvedOrder : state.currentOrder,
        isLoading: false
      }));
    } catch (error: any) {
      console.error('Error approving order:', error);
      set({
        error: error.response?.data?.detail || 'Chyba pri schvaľovaní objednávky',
        isLoading: false
      });
      throw error;
    }
  },

  rejectOrder: async (orderId: number, internal_notes: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await ordersAPI.reject(orderId, internal_notes);
      const rejectedOrder = response.data;

      // Update in orders list
      set(state => ({
        orders: state.orders.map(o => o.id === orderId ? rejectedOrder : o),
        currentOrder: state.currentOrder?.id === orderId ? rejectedOrder : state.currentOrder,
        isLoading: false
      }));
    } catch (error: any) {
      console.error('Error rejecting order:', error);
      set({
        error: error.response?.data?.detail || 'Chyba pri zamietaní objednávky',
        isLoading: false
      });
      throw error;
    }
  },

  fetchStats: async (projectId?: number, assignedProjectId?: number) => {
    try {
      const response = await ordersAPI.getStats(projectId, assignedProjectId);
      set({ stats: response.data });
    } catch (error: any) {
      console.error('Error fetching order stats:', error);
    }
  },

  clearOrders: () => {
    set({ orders: [], currentOrder: null, stats: null, error: null });
  },

  setCurrentOrder: (order: Order | null) => {
    set({ currentOrder: order });
  },
}));

